'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState, type ReactNode } from 'react';

import type { StoreHit } from '@/lib/algolia';
import { BRAND, BRAND_ENUM, IS_GALLET_SHAPED } from '@/lib/cms/config';
import { resolveModelStoryViewModel } from '@/lib/product/resolveModelStory';
import type {
  CMSBanner as CMSBannerType,
  CMSCard as CMSCardType,
  CMSContactUs as CMSContactUsType,
  CMSContentGroup,
  CMSContentItem,
  CMSFaq,
  CMSFaqCategories,
  CMSFindAStore,
  CMSFormCopy,
  CMSHeroSlider as CMSHeroSliderType,
  CMSMagazineArticle,
  CMSPlaceholder,
  CMSPlaceholderModelStory,
  CMSPlaceholderProductDetails,
  CMSPlaceholderStores as CMSPlaceholderStoresType,
  CMSPopup as CMSPopupType,
  CMSProductCard,
  CMSServices as CMSServicesType,
  CMSStoriesList as CMSStoriesListType,
  CMSTimeline,
  CMSTrendingProducts as CMSTrendingProductsType,
} from '@/lib/cms/types';
import {
  CMSComponentType,
  COLLECTION_PRODUCTS_PLACEHOLDER,
  NEWSLETTER_FORM_PLACEHOLDER,
  UG_NEWSLETTER_EXTRA_DATA_KEY,
} from '@/lib/cms/types';
// Legacy types kept for backward compatibility during migration
import type { CardType } from '@/components/cms/Card';
import { localeConfig } from '@/lib/configuration/configuration';
import type { NewsPage } from '@/lib/contentful/news';
import type {
  ContentfulCardFragment,
  ContentGroupFragment,
  ImageTriptychFragment,
} from '@/lib/contentful/types';
import { stripLocaleFromPath, useRouter } from '@/lib/i18n/navigation';
import { devWarn } from '@/lib/utils/devLog';
import { ensureTrailingSlash } from '@/lib/utils/ensureTrailingSlash';
import { replaceLastPathSegment } from '@/lib/utils/replaceLastPathSegment';
import { modalActions } from '@/states/modalState';

// Unified components (following Breitling pattern)
import { Card } from '@/components/cms/Card';
import { ContentGroup } from '@/components/cms/ContentGroup';
import { HeroSlider } from '@/components/cms/HeroSlider';

// Breitling-ported components (from COMPONENT_PORTING_AUDIT)
import { Banner } from '@/components/cms/Banner';
import { CollectionInfoParameter } from '@/components/cms/CollectionInfoParameter';
import { ConditionalComponent } from '@/components/cms/ConditionalComponent';
import { ContactUs } from '@/components/cms/ContactUs';
import { FaqCategories } from '@/components/cms/FaqCategories';
import { FaqWrap } from '@/components/cms/FaqWrap';
import { FreeTextContent } from '@/components/cms/FreeTextContent';
import { Grid, type GridProps } from '@/components/cms/Grid';
import { UniversalGrid, type UniversalGridProps } from '@/components/cms/UniversalGrid';
import { Heading, type HeadingData } from '@/components/cms/Heading';
import { HistoryNavigations } from '@/components/cms/HistoryNavigations';
import { Iframe } from '@/components/cms/Iframe';
import { MediaWrapper, type MediaProp } from '@/components/cms/MediaWrapper';
import { MyAccountCardGroup } from '@/components/cms/MyAccountCardGroup';
import { NavQuickLinks } from '@/components/cms/NavQuickLinks';
import { NewsHeader } from '@/components/cms/NewsHeader';
import { PlaceholderPrint } from '@/components/cms/PlaceholderPrint';
import { PlaceholderService } from '@/components/cms/PlaceholderService';
import { ServicePriceLookupInitialProvider } from '@/components/service/ServicePriceLookup';
import type { CMSRendererExtraData } from '@/lib/cms/extraData';
import { getCmsExtraDataRecord, getCmsLocale } from '@/lib/cms/extraData';
import { SectionHeader } from '@/components/cms/SectionHeader';
import { Services } from '@/components/cms/Services';

// Store/Boutique components
import { PlaceholderStores } from '@/components/cms/PlaceholderStores';

// Layout components
import { DoubleCard } from '@/components/cms/DoubleCard';
import { DoubleCardWide } from '@/components/cms/DoubleCardWide';
import { DynamicLatestNews } from '@/components/cms/DynamicLatestNews/DynamicLatestNews';
import { ExpandableColumnStack } from '@/components/cms/ExpandableColumnStack';
import { FourColumnScroll } from '@/components/cms/FourColumnScroll';
import { ThreeColumnScroll } from '@/components/cms/ThreeColumnScroll';
import { ThreeColumnStack } from '@/components/cms/ThreeColumnStack';

// Slider components
import { SliderFullwidthGallery } from '@/components/cms/SliderFullwidthGallery';
import { SliderMaxFourCards } from '@/components/cms/SliderMaxFourCards';

// Gallet-specific components
import { CategoryBannerGrid } from '@/components/cms/CategoryBannerGrid';
import { DoubleBanner } from '@/components/cms/DoubleBanner';
import { PopupRegistry } from '@/components/cms/Popup/PopupRegistry';
import { StoriesBanner } from '@/components/cms/StoriesBanner';
import { StoriesList } from '@/components/cms/StoriesList';
import { Timeline } from '@/components/cms/Timeline';
import { TrendingProducts } from '@/components/cms/TrendingProducts'; // Also handles SliderProductsManual, AutomaticCarousel

// Breitling-specific components
import {
  CollectionCapsule,
  CollectionGroup,
  CollectionProducts,
  NewsletterForm,
  ProductHero,
  ProductTechnicalData,
} from '@/components/brand';
import type { UgProductHeroProps } from '@/components/brand/ug/ProductHero';
import type { UgCollectionProductsProps } from '@/components/brand/ug/CollectionProducts';
import { resolveUgGalleryImages, resolveUgPackshot, type UgSourceImage } from '@/lib/product/ug';
import { EBoutiqueServices } from '@/components/cms/EBoutiqueServices';
import { ProductGrid } from '@/components/cms/ProductGrid';
import { TutorialBanner } from '@/components/cms/TutorialBanner';
import { ListingFilters, MaterialTabs, type FilterBarPosition } from '@/components/listing';

// PDP-specific components
import {
  ColorVariantSwitcher,
  KeyFeatures,
  PdpBreadcrumbsBar,
  ProductGallery,
  ProductInfo,
  ProductStory,
  RegisterWatch,
  StrapSizeSelector,
  Warranty,
} from '@/components/product';
import type { EyewearColorVariant, StrapSizeVariant } from '@/lib/product/types';
// BuckleProduct type - used for AddToBagDrawer strap flow

import { EntryEffectWrapper } from '@/components/cms/EntryEffectWrapper';
import {
  renderBoutiqueFindAStore,
  renderBoutiquePlaceholder,
  renderPlaceholderBoutiqueDetailAbout,
  renderPlaceholderBoutiquesInfo,
  renderPlaceholderBoutiquesList,
  resolveBoutiqueCardContext,
} from '@/components/cms/CmsRenderer/cmsRendererBoutiqueHelpers';
import styles from './CmsRenderer.module.scss';

const TRIGGER_TYPENAMES = new Set(['Grid', 'SliderProductsManual']);

/**
 * Map a Contentful Grid `maxWidth` string to the Grid component's enum.
 * Unknown or empty values fall through to the component default.
 */
function mapGridMaxWidth(raw: string | null | undefined): GridProps['maxWidth'] {
  if (!raw) return undefined;
  const v = raw.toLowerCase();
  if (v === 'sm' || v === 'md' || v === 'lg' || v === 'xl' || v === 'full') {
    return v;
  }
  return undefined;
}

/**
 * Map a Contentful `contentAlignment` string ('Left'/'Center'/'Right') to the
 * Grid component's `align` enum.
 */
function mapGridAlign(raw: string | null | undefined): GridProps['align'] {
  if (!raw) return undefined;
  switch (raw.toLowerCase()) {
    case 'left':
      return 'start';
    case 'center':
      return 'center';
    case 'right':
      return 'end';
    default:
      return undefined;
  }
}

/**
 * Map a Contentful gap value ('None'/'Small'/'Default'/'Large' or a numeric
 * string) to the Grid component's `gap` token. Mirrors legacy parseColNumber
 * intent — "Default" is the medium default, "None" → smallest, etc.
 */
function mapGridGap(raw: string | null | undefined): GridProps['gap'] {
  if (!raw) return undefined;
  switch (raw.toLowerCase()) {
    case 'none':
      return 'xs';
    case 'small':
      return 'sm';
    case 'default':
    case 'medium':
      return 'md';
    case 'large':
      return 'lg';
    default:
      return undefined;
  }
}

const getEntryEffectVariant = (item: CMSContentItem): 'standard' | 'fade' | 'trigger' => {
  if (TRIGGER_TYPENAMES.has(item.__typename ?? '')) return 'trigger';
  const cardType = (item as { type?: string }).type;
  if (item.__typename === 'Card' && cardType === 'MediaAndSideText') return 'trigger';
  if (item.__typename === 'Card' && cardType === 'MediaAndButtonSide') return 'trigger';
  return 'standard';
};

const isEntryEffectEnabled = (item: CMSContentItem): boolean => {
  return (item as { entryEffect?: boolean }).entryEffect ?? false;
};

/**
 * Default service perks (fallback for PDP)
 */
const DEFAULT_SERVICE_PERKS = [
  { id: 'shipping', icon: 'truck', title: 'Free Shipping' },
  { id: 'returns', icon: 'return', title: 'Free Return' },
  { id: 'gifting', icon: 'gift', title: 'Gifting Service' },
  { id: 'engraving', icon: 'engraving', title: 'Engraving' },
];

/**
 * Map Contentful service perk type to icon name
 */
function mapServicePerkToIcon(type: string | null | undefined): string {
  const lowered = type?.toLowerCase();
  switch (lowered) {
    case 'free shipping':
      return 'truck';
    case 'free return':
      return 'return';
    case 'gift service':
    case 'gifting service':
      return 'gift';
    case 'engraving':
      return 'engraving';
    default:
      return 'truck';
  }
}

interface CMSRendererProps {
  /** Array of CMS content items (unified domain types from lib/cms) */
  content: CMSContentItem[];
  /** Optional extra data (e.g., from Saleor for products) */
  extraData?: CMSRendererExtraData;
}

// ---------------------------------------------------------------------------
// Debug overlay — activated by ?debug=cms
// ---------------------------------------------------------------------------

// Read after mount: useSearchParams would need a Suspense boundary whose
// fallback duplicates every CMS page in the server HTML.
function useDebugCms(): boolean {
  const [debug, setDebug] = useState(false);

  useEffect(() => {
    setDebug(new URLSearchParams(window.location.search).get('debug') === 'cms');
  }, []);

  return debug;
}

function getComponentLabel(item: CMSContentItem): {
  name: string;
  detail?: string;
} {
  const typename = item.__typename as string;

  if (typename === 'Card') {
    const cardType = (item as { type?: string }).type;
    return { name: 'Card', detail: cardType || 'unknown variant' };
  }
  if (typename === 'ContentGroup') {
    const groupType = (item as { type?: string }).type;
    return { name: 'ContentGroup', detail: groupType || undefined };
  }
  if (typename === 'Placeholder') {
    const placeholderType = (item as { type?: string }).type;
    return { name: 'Placeholder', detail: placeholderType || undefined };
  }
  return { name: typename };
}

function DebugLabel({
  item,
  index,
  children,
}: {
  item: CMSContentItem;
  index: number;
  children: ReactNode;
}) {
  const { name, detail } = getComponentLabel(item);
  const internalName = (item as { internalName?: string }).internalName;

  return (
    <div className={styles.debugWrapper} data-debug-typename={item.__typename}>
      <span className={styles.debugLabel}>
        <span className={styles.debugIndex}>{index}</span>
        <strong>{name}</strong>
        {detail && <span className={styles.debugType}>({detail})</span>}
        {internalName && <span className={styles.debugType}>— {internalName}</span>}
      </span>
      {children}
    </div>
  );
}

function DebugNullLabel({ item, index }: { item: CMSContentItem; index: number }) {
  const { name, detail } = getComponentLabel(item);
  return (
    <div className={styles.debugNull}>
      <strong>#{index}</strong> {name}
      {detail ? ` (${detail})` : ''} — <em>rendered null</em>
    </div>
  );
}

/**
 * Valid Card type values from Contentful's native Card content type
 */
const NATIVE_CARD_TYPES: Set<string> = new Set([
  // Core implemented types
  'HeroBanner',
  'BoutiqueHeroBanner',
  'IntroText',
  'MediaAndSideText',
  'MediaAndButtonSide',
  'MediaBanner',
  'InternalHero',
  'FocusedText',
  'NeutralText',
  'DynamicMagazine',
  'CardCapsule',
  'SoloImageFullWidth',
  'SoloImageLargeWithMargin',
  'SoloImageMagazine',
  'MediaAndBottomText',
  'Text',
  'TextWithColumns',
  'Story',
  'SectionBanner',
  'ServiceBanner',
  'Newsletter',
  'PromoBanner', // New canonical name (replaces Newsletter)
  'Icon',
  'TopBanner',
  'SquadCard',
  '3by4Media', // Alias for SquadCard
  // Types needing full implementation (currently fallback to similar variants)
  'ImageTeaser', // Falls back to MediaAndBottomText
  'ImageAndDidYouKnow', // Falls back to MediaAndSideText
  'promoText', // Falls back to Text
  'promoTitle', // Falls back to Text
  'MediaAndWatch', // Falls back to MediaAndSideText
  'NarrowMediaAndBottomText', // Falls back to MediaAndBottomText
  'HistoriesItem', // Falls back to MediaAndBottomText
  'MyAccount', // Falls back to Icon
  'ContactUs', // Falls back to Text
  'contact', // Falls back to Text
  'InsureMyWatch', // Falls back to Text
  'GenericCountdownCard', // Falls back to Text (needs countdown impl)
]);

// ===========================================================================
// Per-typename render context and renderers
//
// `CMSComponent` dispatches a single CMS content item to one of the renderers
// below via `COMPONENT_RENDERERS`. Each renderer handles exactly one
// `__typename` (or a small set of aliases) and receives the shared render
// context so the individual functions stay small and focused. This keeps the
// dispatcher's cognitive complexity flat instead of growing with every new
// component type.
// ===========================================================================

/** Shared context passed to every per-typename renderer. */
type CmsRenderContext = Readonly<{
  item: CMSContentItem;
  extraData?: CMSRendererExtraData;
  /** Raw `item.__typename` cast to string (CMS returns more types than the fragment union). */
  typename: string;
  /** `item.sys.id` (or empty string) — used as the React key. */
  sysId: string;
  /** True when this item is the first in the content list (drives eager preload). */
  isFirstItem: boolean;
  /** Item index — forwarded to components for unique data-testid generation. */
  cmsIndex: number;
}>;

/** `extraData.productDetails` shape used by PlaceholderModelStory → ProductStory. */
type ProductStoryDetailsExtra = {
  productType?: string;
  /**
   * Saleor marketing story from the PDP orchestrator (`buildModelStory`).
   * Watches: com-description-mktg + com-collection. Straps: wbr-name-mktg title,
   * optional wbr-description-mktg body, product media image.
   */
  modelStory?: {
    title: string;
    content: string;
    image?: { url: string; alt: string };
  };
  product?: {
    name?: string;
    sku?: string;
  };
};

// ============================================================================
// HeroSlider - Multi-slide hero carousel (accepts CMSHeroSlider only)
// ============================================================================
function renderHeroSlider({ item, sysId, isFirstItem, cmsIndex }: CmsRenderContext): ReactNode {
  const sliderData = item as CMSHeroSliderType;
  return (
    <HeroSlider
      key={sysId}
      slides={sliderData.slides}
      autoplay={sliderData.autoplay}
      interval={sliderData.interval}
      dynamicBlogHero={sliderData.dynamicBlogHero}
      fixToTop={sliderData.fixToTop}
      preload={isFirstItem}
      cmsIndex={cmsIndex}
    />
  );
}

// Heading - Standalone heading with rich formatting (compatible with CMSHeading)
function renderHeading({ item, sysId }: CmsRenderContext): ReactNode {
  // CMSHeading has same fields as HeadingData: text, headingType, headingSeoTag, etc.
  const headingData = item as HeadingData;
  return <Heading key={sysId} Heading={headingData} />;
}

// ServicesCollection - Service icons/images grid (accepts CMSServices only)
function renderServices({ item, sysId, cmsIndex }: CmsRenderContext): ReactNode {
  const servicesData = item as CMSServicesType;
  return (
    <Services
      key={sysId}
      heading={servicesData.heading}
      services={servicesData.services}
      cmsIndex={cmsIndex}
    />
  );
}

// FaqWrap - FAQ accordion section (accepts CMSFaq only)
function renderFaqWrap({ item, sysId, cmsIndex }: CmsRenderContext): ReactNode {
  const faqData = item as CMSFaq;
  return (
    <FaqWrap
      key={sysId}
      heading={faqData.heading}
      categories={faqData.categories}
      collapse={faqData.collapse}
      cmsIndex={cmsIndex}
    />
  );
}

// Banner - Full-width promotional banner (accepts CMSBanner only)
function renderBanner({ item, sysId, cmsIndex }: CmsRenderContext): ReactNode {
  const bannerData = item as CMSBannerType;
  return (
    <Banner
      key={sysId}
      heading={bannerData.heading}
      body={bannerData.body}
      bannerText={bannerData.bannerText}
      media={bannerData.media}
      cta={bannerData.cta}
      textColor={bannerData.textColor}
      backgroundColor={bannerData.backgroundColor}
      imagePosition={bannerData.imagePosition}
      textAlignment={bannerData.textAlignment}
      cmsIndex={cmsIndex}
    />
  );
}

// ContactUs - Contact form/info section (accepts CMSContactUs only)
function renderContactUs({ item, sysId, cmsIndex }: CmsRenderContext): ReactNode {
  const contactData = item as CMSContactUsType;
  return (
    <ContactUs
      key={sysId}
      type={contactData.type}
      heading={contactData.heading}
      description={contactData.description}
      phone={contactData.phone}
      email={contactData.email}
      cta={contactData.cta}
      backgroundMedia={contactData.backgroundMedia}
      cmsIndex={cmsIndex}
    />
  );
}

// CollectionGroup - Collection showcase with products (Breitling-specific)
// Complex layout with banner, hero products, scrollable row, and end card
function renderCollectionGroup({ item, sysId, cmsIndex }: CmsRenderContext): ReactNode {
  const collectionGroup = item as {
    heading?: string | null;
    type?: string | null;
    banner?: CMSBannerType | null;
    products?: Array<{
      sys: { id: string };
      name: string;
      subtitle?: string;
      sku?: string;
      price?: number;
      priceAmount?: number;
      variantId?: string;
      ctaType?: string;
      label?: string;
      material?: string;
      image?: { url?: string; alt?: string };
      href?: string;
    }>;
    endCard?: {
      sys?: { id: string };
      image?: { url?: string; alt?: string };
      linkUrl?: string;
      linkText?: string;
      linkType?: string;
    };
    collectionName?: string | null;
    tileOverrides?: import('@/lib/cms/types').CMSPlpTileOverride[] | null;
    backgroundColor?: string | null;
    backgroundColorEnd?: string | null;
    backgroundGradientDirection?: string | null;
    anchorSlug?: string | null;
  };

  // If no products, skip rendering
  if (!collectionGroup.products || collectionGroup.products.length === 0) {
    devWarn(`CollectionGroup has no products, skipping render`, {
      id: sysId,
    });
    return null;
  }

  // Render with dedicated CollectionGroup component
  return (
    <CollectionGroup
      key={sysId}
      heading={collectionGroup.heading || collectionGroup.collectionName || undefined}
      type={collectionGroup.type}
      products={collectionGroup.products as CMSProductCard[]}
      tileOverrides={collectionGroup.tileOverrides}
      backgroundColor={collectionGroup.backgroundColor}
      backgroundColorEnd={collectionGroup.backgroundColorEnd}
      backgroundGradientDirection={collectionGroup.backgroundGradientDirection}
      anchorSlug={collectionGroup.anchorSlug}
      sysId={sysId}
      cmsIndex={cmsIndex}
    />
  );
}

// FindAStore — boutique listing uses BoutiquesFindCta; PLPs use PlaceholderStores
function renderFindAStore({ item, sysId, extraData, cmsIndex }: CmsRenderContext): ReactNode {
  const findAStoreItem = item as CMSFindAStore;
  const boutiqueFindCta = renderBoutiqueFindAStore(sysId, findAStoreItem, extraData);

  if (boutiqueFindCta !== undefined) {
    return boutiqueFindCta;
  }

  const stores = extraData?.stores as StoreHit[] | undefined;

  return (
    <PlaceholderStores
      key={sysId}
      type="Default"
      numberOfStores={6}
      backgroundColor={null}
      heading={null}
      findAStore={findAStoreItem.findAStoreData as CMSPlaceholderStoresType['findAStore']}
      stores={stores}
      cmsIndex={cmsIndex}
    />
  );
}

// PlaceholderStores - Boutique slider with store cards (accepts CMSPlaceholderStores)
// Store data is fetched server-side and passed via extraData.stores
function renderPlaceholderStores({
  item,
  sysId,
  extraData,
  cmsIndex,
}: CmsRenderContext): ReactNode {
  const storesData = item as CMSPlaceholderStoresType;
  const stores = extraData?.stores as StoreHit[] | undefined;

  return (
    <PlaceholderStores
      key={sysId}
      type={storesData.type}
      numberOfStores={storesData.numberOfStores}
      backgroundColor={storesData.backgroundColor}
      heading={storesData.heading}
      findAStore={storesData.findAStore}
      stores={stores}
      cmsIndex={cmsIndex}
    />
  );
}

// FreeTextContent - Rich text content block
function renderFreeTextContent({ item, sysId, cmsIndex }: CmsRenderContext): ReactNode {
  return <FreeTextContent key={sysId} {...(item as object)} cmsIndex={cmsIndex} />;
}

// Grid - Flexible grid layout (compatible with CMSGrid)
// CMSGrid.contentCollection.items contains CMSContentItem[] for recursive rendering
function renderGrid({ item, sysId, cmsIndex, extraData }: CmsRenderContext): ReactNode {
  const gridData = item as {
    capsuleRow?: boolean | null;
    columns?: number;
    columnsMobile?: number;
    contentCollection?: { items?: unknown[] };
    paddingTop?: string;
    paddingBottom?: string;
    backgroundColor?: string;
    backgroundMedia?: GridProps['backgroundMedia'];
    verticalAlignment?: GridProps['verticalAlignment'];
    visibleInDesktop?: boolean;
    visibleInMobile?: boolean;
    entryEffect?: boolean;
    slider?: boolean | null;
    reverseMobile?: boolean | null;
    maxWidth?: string | null;
    align?: string | null;
    colGap?: string | null;
    rowGap?: string | null;
  };

  // UG collection-capsule rows are Grids that render as live `o-collectionCapsule`
  // — a flex row with its own container, `CardCapsule` children and a stagger,
  // which the shared CSS-grid layout cannot express. They stay `__typename: 'Grid'`
  // so the live-preview walkers, preview reconciliation, the popup-id collector
  // and `TRIGGER_TYPENAMES` all keep treating them as grids; only rendering
  // branches. Resolves to `RenderNull` on Gallet and Breitling.
  if (gridData.capsuleRow) {
    return <CollectionCapsule key={sysId} data={item} cmsIndex={cmsIndex} />;
  }

  return (
    <Grid
      key={sysId}
      id={item.anchorId}
      columns={gridData.columns as 1 | 2 | 3 | 4}
      columnsMobile={gridData.columnsMobile as 1 | 2}
      contentCollection={gridData.contentCollection as GridProps['contentCollection']}
      paddingTop={gridData.paddingTop}
      paddingBottom={gridData.paddingBottom}
      backgroundColor={gridData.backgroundColor}
      backgroundMedia={gridData.backgroundMedia}
      verticalAlignment={gridData.verticalAlignment}
      visibleInDesktop={gridData.visibleInDesktop}
      visibleInMobile={gridData.visibleInMobile}
      entryEffect={gridData.entryEffect ?? false}
      slider={gridData.slider ?? undefined}
      reverseMobile={gridData.reverseMobile ?? undefined}
      maxWidth={mapGridMaxWidth(gridData.maxWidth)}
      align={mapGridAlign(gridData.align)}
      gap={mapGridGap(gridData.colGap)}
      cmsIndex={cmsIndex}
      extraDataRecord={getCmsExtraDataRecord(extraData)}
    />
  );
}

// UniversalGrid - new-generation layout grid (renders its own UniversalCards)
function renderUniversalGrid({ item, sysId, cmsIndex }: CmsRenderContext): ReactNode {
  const gridProps = item as unknown as UniversalGridProps;
  return <UniversalGrid key={sysId} {...gridProps} cmsIndex={cmsIndex} />;
}

// Iframe - Embedded external content
function renderIframe({ item, sysId }: CmsRenderContext): ReactNode {
  return <Iframe key={sysId} {...(item as object)} />;
}

// NavQuickLinks - Navigation quick links (uses individual props + sys)
function renderNavQuickLinks({ item, sysId, cmsIndex }: CmsRenderContext): ReactNode {
  // Cast to any first then to NavQuickLinksProps to satisfy TypeScript
  // The item already has sys from ContentfulFragment
  const navProps = item as unknown as {
    sys: { id: string };
    [key: string]: unknown;
  };
  return <NavQuickLinks key={sysId} {...navProps} cmsIndex={cmsIndex} />;
}

// PlaceholderService - Service placeholder
function renderPlaceholderServiceComponent({
  item,
  sysId,
  extraData,
}: CmsRenderContext): ReactNode {
  return (
    <PlaceholderService
      key={sysId}
      {...(item as object)}
      locale={getCmsLocale(extraData)}
      serviceRequestEnabled={extraData?.serviceRequestEnabled === true}
      ecsAvailable={extraData?.ecsAvailable === true}
    />
  );
}

// FaqCategories - FAQ with sidebar navigation
function renderFaqCategories({ item, sysId }: CmsRenderContext): ReactNode {
  const faqCategories = item as CMSFaqCategories;
  return (
    <FaqCategories
      key={sysId}
      categoryLabel={faqCategories.categoryLabel}
      faqWraps={faqCategories.faqWraps}
    />
  );
}

// HistoryNavigations - Prev/next navigation links
function renderHistoryNavigations({ item, sysId }: CmsRenderContext): ReactNode {
  return <HistoryNavigations key={sysId} {...(item as object)} />;
}

// ConditionalComponent - Auth/empty state conditionals
function renderConditionalComponent({ item, sysId }: CmsRenderContext): ReactNode {
  return <ConditionalComponent key={sysId} {...(item as object)} />;
}

// SectionHeader - Styled headings
function renderSectionHeader({ item, sysId }: CmsRenderContext): ReactNode {
  return <SectionHeader key={sysId} {...(item as object)} />;
}

// NewsHeader - News page header with breadcrumb
function renderNewsHeader({ item, sysId }: CmsRenderContext): ReactNode {
  return <NewsHeader key={sysId} {...(item as object)} />;
}

// PlaceholderPrint - Print button utility
function renderPlaceholderPrint({ sysId }: CmsRenderContext): ReactNode {
  return <PlaceholderPrint key={sysId} />;
}

// CollectionInfoParameter - Collection info cards
function renderCollectionInfoParameter({ item, sysId, cmsIndex }: CmsRenderContext): ReactNode {
  return <CollectionInfoParameter key={sysId} {...(item as object)} cmsIndex={cmsIndex} />;
}

// MyAccountCardGroup - Account page cards (uses data prop)
function renderMyAccountCardGroup({ item, sysId, cmsIndex }: CmsRenderContext): ReactNode {
  return <MyAccountCardGroup key={sysId} data={item as never} cmsIndex={cmsIndex} />;
}

// MediaWrapper - Image/video with focal point (standalone CMS section)
function renderMediaWrapper({ item, sysId }: CmsRenderContext): ReactNode {
  return <MediaWrapper key={sysId} media={item as MediaProp} />;
}

// ============================================================================
// Layout components (multi-item containers)
// ============================================================================

// DoubleCard - 2-4 column grid (uses individual props)
function renderDoubleCard({ item, sysId, extraData }: CmsRenderContext): ReactNode {
  return (
    <DoubleCard
      key={sysId}
      {...(item as object)}
      extraDataRecord={getCmsExtraDataRecord(extraData)}
    />
  );
}

// DoubleCardWide - Wide 2-column variant (uses individual props)
function renderDoubleCardWide({ item, sysId, extraData }: CmsRenderContext): ReactNode {
  return (
    <DoubleCardWide
      key={sysId}
      {...(item as object)}
      extraDataRecord={getCmsExtraDataRecord(extraData)}
    />
  );
}

// ThreeColumnStack - 3-column responsive grid (uses individual props)
function renderThreeColumnStack({ item, sysId, extraData }: CmsRenderContext): ReactNode {
  return (
    <ThreeColumnStack
      key={sysId}
      {...(item as object)}
      extraDataRecord={getCmsExtraDataRecord(extraData)}
    />
  );
}

// ThreeColumnScroll - 3-column with mobile scroll (uses data prop)
function renderThreeColumnScroll({ item, sysId, cmsIndex }: CmsRenderContext): ReactNode {
  return <ThreeColumnScroll key={sysId} data={item as never} cmsIndex={cmsIndex} />;
}

// FourColumnScroll - 4-column with mobile scroll (uses data prop)
function renderFourColumnScroll({ item, sysId, cmsIndex }: CmsRenderContext): ReactNode {
  return <FourColumnScroll key={sysId} data={item as never} cmsIndex={cmsIndex} />;
}

// ============================================================================
// Slider components
// ============================================================================

// SliderMaxFourCards - Swiper carousel with Card items (uses data prop)
function renderSliderMaxFourCards({ item, sysId, cmsIndex }: CmsRenderContext): ReactNode {
  return <SliderMaxFourCards key={sysId} data={item as never} cmsIndex={cmsIndex} />;
}

// SliderFullwidthGallery - Full-width Swiper gallery (uses data prop)
function renderSliderFullwidthGallery({ item, sysId, cmsIndex }: CmsRenderContext): ReactNode {
  return (
    <SliderFullwidthGallery key={sysId} data={item as ContentGroupFragment} cmsIndex={cmsIndex} />
  );
}

type SliderHeading = NonNullable<CMSTrendingProductsType['heading']>;
type AutomaticSliderData = {
  products: CMSTrendingProductsType['products'];
  heading: SliderHeading | undefined;
};

/**
 * Config for `SliderProductsAutomatic` items whose products come from
 * orchestrator-provided `extraData` (e.g. PDP "You May Also Like") rather than
 * the CMS entry. Each entry matches one or more CMS `sliderType` values,
 * extracts + normalises its data source, and supplies a default heading.
 */
const AUTOMATIC_SLIDER_SOURCES: ReadonlyArray<{
  match: (sliderType: string) => boolean;
  getData: (extraData: Record<string, unknown> | undefined) => AutomaticSliderData | undefined;
  fallbackHeading: SliderHeading;
}> = [
  {
    match: (t) => t === 'you may also like' || t === 'youmayalsolike',
    getData: (extraData) => {
      const data = extraData?.trendingProducts as CMSTrendingProductsType | undefined;
      return data?.products
        ? { products: data.products, heading: data.heading ?? undefined }
        : undefined;
    },
    fallbackHeading: { text: 'You May Also Like' },
  },
  {
    match: (t) => t === 'compatible straps' || t === 'add an extra strap',
    getData: (extraData) => {
      const data = extraData?.extraStraps as CMSTrendingProductsType | undefined;
      return data?.products
        ? { products: data.products, heading: data.heading ?? undefined }
        : undefined;
    },
    fallbackHeading: { text: 'Add an Extra Strap' },
  },
  {
    match: (t) => t === 'recently viewed',
    getData: (extraData) => {
      const products = extraData?.recentlyViewed as CMSProductCard[] | undefined;
      return products && products.length >= 2 ? { products, heading: undefined } : undefined;
    },
    fallbackHeading: { text: 'Recently Viewed' },
  },
];

/**
 * Resolve products + heading for an automatic product slider whose products are
 * supplied via `extraData`. Returns `undefined` when no matching source has
 * data, leaving the caller's CMS values untouched. An unresolved CMS heading
 * placeholder (e.g. "[[The]]") is ignored in favour of the source/default.
 */
function resolveAutomaticSliderData(
  sliderData: CMSTrendingProductsType,
  extraData: Record<string, unknown> | undefined
): AutomaticSliderData | undefined {
  const sliderType = sliderData.sliderType?.toLowerCase() ?? '';
  const config = AUTOMATIC_SLIDER_SOURCES.find((source) => source.match(sliderType));
  const resolved = config?.getData(extraData);
  if (!config || !resolved) {
    return undefined;
  }

  const headingText = sliderData.heading?.text;
  const cmsHeadingHasPlaceholder = headingText ? headingText.includes('[[') : false;
  const heading =
    (cmsHeadingHasPlaceholder ? undefined : sliderData.heading) ||
    resolved.heading ||
    config.fallbackHeading;

  return { products: resolved.products, heading };
}

// SliderProductsManual / SliderProductsAutomatic - Product carousel
// Products are populated by the orchestrator based on SKUs or slider type
function renderSliderProducts({
  item,
  typename,
  sysId,
  extraData,
  cmsIndex,
}: CmsRenderContext): ReactNode {
  const sliderData = item as CMSTrendingProductsType;

  // For automatic sliders without products, pull them from orchestrator extraData.
  // This handles cases like "You May Also Like" on PDPs.
  let products = sliderData.products;
  let headingData = sliderData.heading || undefined;

  if ((!products || products.length === 0) && typename === 'SliderProductsAutomatic') {
    const resolved = resolveAutomaticSliderData(sliderData, extraData);
    if (resolved) {
      products = resolved.products;
      headingData = resolved.heading;
    }
  }

  // Don't render if still no products
  if (!products || products.length === 0) {
    return null;
  }

  return (
    <TrendingProducts
      key={sysId}
      headingData={headingData}
      products={products}
      automaticType={sliderData.sliderType as 'recently viewed' | 'you may also like' | undefined}
      backgroundColor={sliderData.backgroundColor || undefined}
      backgroundImage={
        sliderData.backgroundImage
          ? {
              url: sliderData.backgroundImage.url || '',
              alt: sliderData.backgroundImage.alt || undefined,
            }
          : undefined
      }
      productCardBackgroundColor={sliderData.productCardBackgroundColor || undefined}
      slidesPerView={sliderData.slidesPerView ?? undefined}
      slidesPerViewMobile={sliderData.slidesPerViewMobile ?? undefined}
      showCta={sliderData.showCta ?? undefined}
      showPrice={sliderData.showPrice ?? undefined}
      showName={sliderData.showName ?? undefined}
      showWishlist={sliderData.showWishlist ?? undefined}
      ctaText={sliderData.ctaText}
      ctaUrl={sliderData.ctaUrl}
      ctaStyle={sliderData.ctaStyle}
      ctaAriaLabel={sliderData.ctaAriaLabel}
      cmsIndex={cmsIndex}
    />
  );
}

// ============================================================================
// Card variants - Single content sections with media/text
// ============================================================================

// Handle native Contentful Card content type (from CMS)
function renderCard({
  item,
  sysId,
  extraData,
  isFirstItem,
  cmsIndex,
}: CmsRenderContext): ReactNode {
  const cardItem = item as ContentfulCardFragment;
  const cardType = cardItem.type as CardType | undefined;

  // Check if it's a valid card type
  if (cardType && NATIVE_CARD_TYPES.has(cardType)) {
    const { boutiquesListing, boutiqueDetail } = resolveBoutiqueCardContext(cardType, extraData);

    // The magazine feed is fetched by the route, not linked on the card.
    const magazineArticles =
      cardType === 'DynamicMagazine'
        ? (extraData?.magazineArticles as CMSMagazineArticle[] | undefined)
        : undefined;

    return (
      <Card
        key={sysId}
        id={item.anchorId}
        type={cardType}
        data={item}
        preload={isFirstItem}
        cmsIndex={cmsIndex}
        boutiquesListing={boutiquesListing}
        boutiqueDetail={boutiqueDetail}
        magazineArticles={magazineArticles}
      />
    );
  }

  // Unknown card type - log it in development
  devWarn(`Unknown Card type: ${cardType}`);
  return null;
}

// Handle ContentGroup - adapter output ONLY (CMSContentGroup)
function renderContentGroup({ item, sysId, extraData, cmsIndex }: CmsRenderContext): ReactNode {
  const groupItem = item as CMSContentGroup;

  // Items from adapter - already transformed
  const items = groupItem.items || [];

  // Get original type from adapter (DoubleCard, ThreeColumnStack, etc.)
  // Falls back to mapping layout to type if original type not present
  const layoutToType: Record<string, string> = {
    grid: 'ThreeColumnStack',
    stack: 'ThreeColumnStack',
    carousel: 'ThreeColumnScroll',
  };
  const groupType =
    groupItem.type || (groupItem.layout ? layoutToType[groupItem.layout] : 'ThreeColumnStack');

  // Determine if items are media-only (ImageWithFocalPoint) or full cards
  const firstItem = items[0];
  const isMediaOnly = (firstItem as { __typename?: string })?.__typename === 'ImageWithFocalPoint';

  // For ExpandableColumnStack - Gallet interactive 3-element component
  // Requires exactly 3 items with video support
  if (groupType === 'ExpandableColumnStack') {
    return <ExpandableColumnStack key={sysId} items={items} cmsIndex={cmsIndex} />;
  }

  // SliderFullwidthGallery — Swiper-based full-width image gallery
  if (groupType === 'SliderFullwidthGallery') {
    return <SliderFullwidthGallery key={sysId} data={groupItem} cmsIndex={cmsIndex} />;
  }

  // DynamicLatestNews — Gallet two-panel news carousel with world clock wheel
  if (groupType === 'DynamicLatestNews') {
    const latestNews = extraData?.latestNews as NewsPage[] | undefined;
    if (!latestNews?.length) return null;

    // Extract static content from the SectionBanner card in items
    const card = items[0] as CMSCardType | undefined;
    const staticContent = {
      backgroundImage: {
        src: card?.image?.url || '',
        alt: card?.image?.alt || '',
      },
      subtitle: card?.subtitleAbove || card?.heading || '',
      heading: card?.subHeading || card?.body || '',
      ctaLabel: card?.cta?.text || '',
      ctaUrl: card?.cta?.url || '',
    };

    // Map news pages to slide format
    const slides = latestNews.map((news) => ({
      id: news.sys.id,
      thumbnail: {
        src: news.thumbnail?.image?.url || '',
        alt: news.thumbnail?.alt || '',
      },
      timezoneLabel: news.timezone || '',
      cityNames: (() => {
        const parts = (news.timezone || '').split(' / ');
        return {
          primary: parts[0]?.trim() || '',
          secondary: parts[1]?.trim() || '',
        };
      })(),
      articleTitle: news.pageTitle || '',
      articleUrl: news.slug ? `/${news.slug}` : '',
    }));

    return <DynamicLatestNews key={sysId} staticContent={staticContent} slides={slides} />;
  }

  // For MyAccountCardGroup, use the dedicated component
  if (groupType === 'MyAccountCardGroup') {
    return <MyAccountCardGroup key={sysId} data={groupItem as never} cmsIndex={cmsIndex} />;
  }

  // For DoubleCard with Card items, render each card individually
  if (groupType === 'DoubleCard' && !isMediaOnly) {
    return (
      <ContentGroup
        key={sysId}
        id={item.anchorId}
        type="DoubleCard"
        items={items}
        cardType="MediaBanner"
        heading={groupItem.heading || undefined}
        cmsIndex={cmsIndex}
      />
    );
  }

  // For DoubleCardPadded — same as DoubleCard but with white background + outer padding
  if (groupType === 'DoubleCardPadded' && !isMediaOnly) {
    return (
      <ContentGroup
        key={sysId}
        id={item.anchorId}
        type="DoubleCardPadded"
        items={items}
        cardType="MediaBanner"
        heading={groupItem.heading || undefined}
        cmsIndex={cmsIndex}
      />
    );
  }

  return (
    <ContentGroup
      key={sysId}
      id={item.anchorId}
      type={groupType as 'ThreeColumnStack'}
      items={items}
      heading={groupItem.heading || undefined}
      mediaOnly={isMediaOnly}
      cmsIndex={cmsIndex}
    />
  );
}

// ============================================================================
// ContentGroup - ImageTriptych → ThreeColumnStack
// ============================================================================
function renderImageTriptych({ item, sysId, cmsIndex }: CmsRenderContext): ReactNode {
  const triptych = item as ImageTriptychFragment;
  const images = [triptych.leftImage, triptych.centerImage, triptych.rightImage]
    .filter(Boolean)
    .map((img, i) => ({
      ...img,
      sys: { id: `${sysId}-img-${i}` },
      __typename: 'ImageWithFocalPoint' as const,
    }));

  return (
    <ContentGroup
      key={sysId}
      type="ThreeColumnStack"
      items={images}
      mediaOnly
      cmsIndex={cmsIndex}
    />
  );
}

// ============================================================================
// Gallet-specific components - Stay as they are
// ============================================================================

// DoubleBanner - 2-panel promotional layout (could be ContentGroup DoubleCard)
function renderDoubleBanner({ item, sysId, cmsIndex }: CmsRenderContext): ReactNode {
  return <DoubleBanner key={sysId} data={item as never} cmsIndex={cmsIndex} />;
}

// CategoryBannerGrid - Collection cards grid (could be Grid + Card)
function renderCategoryBannerGrid({ item, sysId, cmsIndex }: CmsRenderContext): ReactNode {
  return <CategoryBannerGrid key={sysId} data={item as never} cmsIndex={cmsIndex} />;
}

// TrendingProducts - Product slider (accepts CMSTrendingProducts only)
function renderTrendingProducts({ item, sysId, cmsIndex }: CmsRenderContext): ReactNode {
  const trendingData = item as CMSTrendingProductsType;
  return (
    <TrendingProducts
      key={sysId}
      headingData={trendingData.heading || undefined}
      products={trendingData.products}
      cmsIndex={cmsIndex}
    />
  );
}

// StoriesBanner - World clock + featured story (Gallet-specific design)
function renderStoriesBanner({ item, sysId, cmsIndex }: CmsRenderContext): ReactNode {
  return <StoriesBanner key={sysId} data={item as never} cmsIndex={cmsIndex} />;
}

// StoriesList - Grid of story cards with optional title and filters (Gallet-specific)
function renderStoriesList({ item, sysId, extraData }: CmsRenderContext): ReactNode {
  const storiesListData = item as CMSStoriesListType;

  const stories = (storiesListData.latest ? extraData?.latestNews : extraData?.allNews) as
    | NewsPage[]
    | undefined;
  if (!stories?.length) return null;

  return (
    <StoriesList
      key={sysId}
      stories={stories}
      latest={storiesListData.latest}
      title={storiesListData.title}
      filters={storiesListData.filters}
      buttonLabel={storiesListData.buttonLabel}
      buttonUrl={storiesListData.buttonUrl}
    />
  );
}

// Timeline - Brand history timeline (Gallet-specific)
function renderTimeline({ item, sysId, cmsIndex }: CmsRenderContext): ReactNode {
  const timeline = item as CMSTimeline;
  const entryPopups = (timeline.entries ?? [])
    .map((entry) => entry.cta)
    .filter((cta): cta is NonNullable<typeof cta> => cta !== null && cta !== undefined);

  return (
    <>
      {entryPopups.map((popup) => (
        <PopupRegistry
          key={popup.sys.id}
          popupId={popup.sys.id}
          animation={popup.animation}
          maxWidth={popup.maxWidth}
          backgroundColor={popup.backgroundColor}
        >
          {popup.content ? <CMSRenderer content={popup.content} /> : null}
        </PopupRegistry>
      ))}
      <Timeline
        key={sysId}
        id={timeline.id}
        entries={timeline.entries}
        ctaLabel={timeline.ctaLabel}
        cmsIndex={cmsIndex}
        stickyScroll={!!timeline.stickyScroll}
      />
    </>
  );
}

// EBoutiqueServices - Service icons grid (Gallet-specific)
function renderEBoutiqueServices({ item, sysId, cmsIndex }: CmsRenderContext): ReactNode {
  return <EBoutiqueServices key={sysId} data={item as never} cmsIndex={cmsIndex} />;
}

function renderPopup({ item, sysId }: CmsRenderContext): ReactNode {
  const popupData = item as CMSPopupType;
  return (
    <PopupRegistry
      key={sysId}
      popupId={popupData.sys.id}
      animation={popupData.animation}
      maxWidth={popupData.maxWidth}
      backgroundColor={popupData.backgroundColor}
    >
      {popupData.content ? <CMSRenderer content={popupData.content} /> : null}
    </PopupRegistry>
  );
}

// ============================================================================
// PDP Placeholder Types - Product Detail Page template markers
// These render product-specific content using extraData.productDetails
// Adapter output ONLY (CMSPlaceholderProductDetails)
// ============================================================================
/**
 * The render functions below are dispatched by __typename, so they cannot call
 * hooks themselves — the call order is not stable. Anything needing one is a
 * component rendered as JSX, like this.
 */
function BuckleNotice({ className }: Readonly<{ className?: string }>) {
  const t = useTranslations('product');
  return <p className={className}>{t('buckleNotIncluded')}</p>;
}

type StrapSizeSelectorNavProps = Readonly<{
  sizeVariants: StrapSizeVariant[];
  selectedSize: StrapSizeVariant;
  className?: string;
}>;

function StrapSizeSelectorNav({
  sizeVariants,
  selectedSize,
  className,
}: StrapSizeSelectorNavProps) {
  const router = useRouter();

  const handleSizeSelect = (variant: StrapSizeVariant) => {
    const pathWithoutLocale = stripLocaleFromPath(globalThis.location.pathname);
    router.push(ensureTrailingSlash(replaceLastPathSegment(pathWithoutLocale, variant.sku)));
  };

  return (
    <StrapSizeSelector
      sizeVariants={sizeVariants}
      selectedSize={selectedSize}
      onSizeSelect={handleSizeSelect}
      className={className}
    />
  );
}

/**
 * UG's PDP hero. Shares the Contentful type name with Gallet and
 * nothing else — no cart, no financing, no engraving, no strap drawer. Kept as
 * a separate function so the Gallet renderer below is untouched.
 */
function renderUgProductHero({ item, sysId, extraData }: CmsRenderContext): ReactNode {
  const details = extraData?.productDetails as
    | {
        product?: Omit<UgProductHeroProps['product'], 'images'> & { images: UgSourceImage[] };
        productClass?: UgProductHeroProps['productClass'];
        ugRelatedProducts?: UgProductHeroProps['relatedProducts'];
        ugCollection?: UgProductHeroProps['collection'];
        galleryImagesResolved?: boolean;
        preOrderEnabled?: boolean;
      }
    | undefined;

  if (!details?.product) {
    devWarn('PlaceholderProductDetails requires extraData.productDetails');
    return null;
  }

  const productClass = details.productClass ?? 'collection';
  const images = details.galleryImagesResolved
    ? details.product.images
    : resolveUgGalleryImages(details.product.images);
  // The packshot is the frame the gallery selector drops, so it has to come off
  // the unresolved media. The couture path arrives pre-resolved and has no
  // pre-order CTA, so there is nothing to recover there.
  const packshot = details.galleryImagesResolved ? null : resolveUgPackshot(details.product.images);

  return (
    <ProductHero
      key={sysId}
      product={{
        ...details.product,
        images,
        packshot,
      }}
      productClass={productClass}
      relatedProducts={details.ugRelatedProducts ?? []}
      collection={details.ugCollection ?? null}
      // `?? true` for the same reason the route computes it that way: the
      // couture page builds this extraData without the flag, and a missing
      // value must not blank the CTA.
      preOrderEnabled={details.preOrderEnabled ?? true}
      config={item as CMSPlaceholderProductDetails}
    />
  );
}

function renderPlaceholderProductDetails(context: CmsRenderContext): ReactNode {
  // Guard clause, not a branch — Gallet's renderer below is unchanged.
  if (BRAND === BRAND_ENUM.Ug) {
    return renderUgProductHero(context);
  }

  const { item, sysId, extraData } = context;
  const productDetails = extraData?.productDetails as
    | {
        product: {
          id: string;
          name: string;
          sku?: string;
          images: Array<{ url: string; alt: string }>;
          shortDescription?: string;
          lugWidth?: number;
          [key: string]: unknown;
        };
        productType?: 'watch' | 'strap' | 'eyewear';
        servicePerks?: Array<{
          id: string;
          icon: string;
          title: string;
          type?: string;
          modalText?: { json: unknown };
          linkUrl?: string;
          body?: string;
        }>;
        breadcrumbs?: Array<{ label: string; href?: string }>;
        engravingEnabled?: boolean;
        giftAvailable?: boolean;
        affirmEnabled?: boolean;
        affirmPaymentMax?: number;
        cembraEnabled?: boolean;
        cembraFinancingPaymentMax?: number;
        countryCode?: string;
        v12Enabled?: boolean;
        v12PaymentMin?: number;
        v12PaymentMax?: number;
        oneyEnabled?: boolean;
        oney3xMin?: number;
        oney3xMax?: number;
        oney4xMin?: number;
        oney4xMax?: number;
        boutiquePickupAvailable?: boolean;
        showCallToBuy?: boolean;
        isCatalogMode?: boolean;
        showAvailabilityRequest?: boolean;
        showNotifyMe?: boolean;
        // Strap-specific
        sizeVariants?: StrapSizeVariant[];
        compatibleBuckleProducts?: Array<{ sku?: string }>;
        integratedBuckle?: boolean;
      }
    | undefined;

  if (!productDetails?.product) {
    devWarn('PlaceholderProductDetails requires extraData.productDetails');
    return null;
  }

  const isStrap = productDetails.productType === 'strap';

  // Use perks from CMS content or extraData
  // oxlint-disable-next-line typescript/no-explicit-any
  const cmsPerks = (item as any).perks;
  const servicePerks =
    cmsPerks?.length > 0
      ? cmsPerks.map(
          (perk: {
            sys?: { id: string };
            type?: string;
            title?: string;
            modalText?: unknown;
            url?: string;
          }) => ({
            id: perk.sys?.id || '',
            icon: mapServicePerkToIcon(perk.type),
            title: perk.title || '',
            type: perk.type,
            modalText: perk.modalText,
            linkUrl: perk.url,
          })
        )
      : productDetails.servicePerks || DEFAULT_SERVICE_PERKS;

  // Get breadcrumbs from extraData (generated in page component)
  const breadcrumbs = productDetails.breadcrumbs;

  // Build product name with lug width for straps
  const displayName =
    isStrap && productDetails.product.lugWidth
      ? `${productDetails.product.name} - ${productDetails.product.lugWidth} MM`
      : productDetails.product.name;

  return (
    <div key={sysId}>
      <div className={styles.detailsContainer}>
        {IS_GALLET_SHAPED && breadcrumbs && breadcrumbs.length > 0 && (
          <PdpBreadcrumbsBar items={breadcrumbs} />
        )}

        {/* Product Detail Section */}
        <section className={styles.productDetail}>
          <ProductGallery
            key={productDetails.product.sku}
            images={productDetails.product.images}
            productName={displayName}
            badge={(productDetails.product as { badge?: string }).badge}
            className={styles.gallery}
            productSku={productDetails.product.sku}
            productPrice={(productDetails.product as { price?: { amount: number } }).price?.amount}
            productCurrency={
              (productDetails.product as { price?: { currency: string } }).price?.currency
            }
            productType={isStrap ? 'strap' : 'watch'}
            ctaType={(productDetails.product as { ctaType?: string }).ctaType}
          />
          <div className={styles.info}>
            <ProductInfo
              product={{ ...productDetails.product, name: displayName } as never}
              servicePerks={servicePerks}
              breadcrumbs={breadcrumbs}
              engravingEnabled={productDetails.engravingEnabled}
              giftAvailable={productDetails.giftAvailable}
              affirmEnabled={productDetails.affirmEnabled}
              affirmPaymentMax={productDetails.affirmPaymentMax}
              cembraEnabled={productDetails.cembraEnabled}
              cembraFinancingPaymentMax={productDetails.cembraFinancingPaymentMax}
              countryCode={productDetails.countryCode}
              v12Enabled={productDetails.v12Enabled}
              v12PaymentMin={productDetails.v12PaymentMin}
              v12PaymentMax={productDetails.v12PaymentMax}
              oneyEnabled={productDetails.oneyEnabled}
              oney3xMin={productDetails.oney3xMin}
              oney3xMax={productDetails.oney3xMax}
              oney4xMin={productDetails.oney4xMin}
              oney4xMax={productDetails.oney4xMax}
              boutiquePickupAvailable={productDetails.boutiquePickupAvailable}
              showCallToBuy={productDetails.showCallToBuy}
              isCatalogMode={productDetails.isCatalogMode}
              showAvailabilityRequest={productDetails.showAvailabilityRequest}
              showNotifyMe={productDetails.showNotifyMe}
              onAddToBag={
                isStrap
                  ? () => {
                      // Open CartStrapDrawer in PDP mode with pre-selected strap
                      const product = productDetails.product;
                      const pricing = (
                        product as {
                          price?: { amount: number; currency: string };
                        }
                      ).price;

                      // Include the current SKU — the drawer re-queries sizes after a
                      // size change and must not drop the original (BEC-24767).
                      const otherSizeSKUs =
                        productDetails.sizeVariants?.map((v) => v.sku).filter(Boolean) || [];

                      // Use raw buckle SKUs from orchestrator (variant SKUs for Saleor queries)
                      const buckleSKUs =
                        (
                          productDetails as {
                            compatibleBuckleSkus?: string[];
                          }
                        ).compatibleBuckleSkus || [];
                      const locale = extraData?.locale as string | undefined;
                      const channelSlug = locale ? localeConfig.getChannelSlug(locale) : 'us-usd';

                      modalActions.open('cart-strap-drawer', {
                        orderCtaType:
                          (product.ctaType as 'ORDER' | 'PRE_ORDER' | undefined) || 'ORDER',
                        // Channel slug format: 'us-usd', derived from locale like 'us-en'
                        channel: channelSlug,
                        // PDP mode fields
                        selectedStrapVariantId: product.id,
                        selectedStrapSku: product.sku || '',
                        selectedStrapName: displayName,
                        selectedStrapImage: product.images?.[0]?.url || '',
                        selectedStrapPrice: pricing?.amount || 0,
                        selectedStrapCurrency: pricing?.currency || 'USD',
                        selectedStrapSlug: (product as { slug?: string }).slug || '',
                        hasIntegratedBuckle: productDetails.integratedBuckle ?? false,
                        // Pass SKUs so drawer can fetch sizes with proper Saleor query
                        otherSizeSKUs,
                        // Pass buckle SKUs so drawer can fetch them with useBucklesAndTools hook
                        compatibleBuckleSKUs: buckleSKUs,
                      });
                    }
                  : undefined
              }
              sizeSelector={
                isStrap && productDetails.sizeVariants && productDetails.sizeVariants.length > 0 ? (
                  <StrapSizeSelectorNav
                    sizeVariants={productDetails.sizeVariants}
                    selectedSize={
                      productDetails.sizeVariants.find(
                        (v) => v.sku === productDetails.product.sku
                      ) || productDetails.sizeVariants[0]
                    }
                    className={styles.sizeSelector}
                  />
                ) : undefined
              }
            />

            {/* Strap-specific: Description */}
            {isStrap && productDetails.product.shortDescription && (
              <p className={styles.strapDescription}>{productDetails.product.shortDescription}</p>
            )}

            {/* Strap-specific: Buckle Not Included notice */}
            {isStrap && productDetails.integratedBuckle === false && (
              <BuckleNotice className={styles.buckleNotice} />
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

// ============================================================================
// Eyewear PDP Placeholder - PlaceholderEyewearDetails
// Renders eyewear-specific hero with color variant switcher
// NOTE: Straps are handled in PlaceholderProductDetails above (productType check)
// ============================================================================
function renderPlaceholderEyewearDetails({ sysId, extraData }: CmsRenderContext): ReactNode {
  const productDetails = extraData?.productDetails as
    | {
        product: {
          id: string;
          name: string;
          sku: string;
          images: Array<{ url: string; alt: string }>;
          price: { amount: number; currency: string; formatted: string };
          ctaType?: string;
          [key: string]: unknown;
        };
        productType: 'eyewear';
        breadcrumbs?: Array<{ label: string; href?: string }>;
        showCallToBuy?: boolean;
        colorVariants?: EyewearColorVariant[];
      }
    | undefined;

  if (!productDetails?.product) {
    devWarn('PlaceholderEyewearDetails requires extraData.productDetails');
    return null;
  }

  const breadcrumbs = productDetails.breadcrumbs;
  const colorVariants = productDetails.colorVariants || [];

  // Current color is the one matching current SKU
  const currentColor =
    colorVariants.find((v) => v.sku === productDetails.product.sku) || colorVariants[0] || null;

  return (
    <div key={sysId} className={styles.detailsContainer}>
      {/* Product Detail Section - Gallery + Info */}
      <section className={styles.productDetail}>
        <ProductGallery
          key={productDetails.product.sku}
          images={productDetails.product.images}
          productName={productDetails.product.name}
          className={styles.gallery}
          productSku={productDetails.product.sku}
          productPrice={(productDetails.product as { price?: { amount: number } }).price?.amount}
          productCurrency={
            (productDetails.product as { price?: { currency: string } }).price?.currency
          }
          productType="eyewear"
          ctaType={(productDetails.product as { ctaType?: string }).ctaType}
        />
        <div className={styles.info}>
          {/* Product Info with eyewear-specific extensions */}
          <ProductInfo
            product={productDetails.product as never}
            breadcrumbs={breadcrumbs}
            showCallToBuy={productDetails.showCallToBuy}
            className={styles.eyewearInfo}
          />

          {/* Color Variant Switcher */}
          {colorVariants.length > 0 && (
            <ColorVariantSwitcher
              colorVariants={colorVariants}
              currentVariant={currentColor}
              className={styles.colorSwitcher}
            />
          )}
        </div>
      </section>
    </div>
  );
}

/**
 * CMS `PlaceholderModelStory` → ProductStory.
 *
 * Presence/position: CMS only (no Saleor injection of story placeholders).
 * Always renders the section shell when this block exists (even if copy/image are empty).
 *
 * Field merge (see `resolveModelStoryViewModel`):
 * - title / content — Saleor `modelStory` first, then CMS `storyTitle` / `storyContent`
 * - image — CMS `media` first, then Saleor `modelStory.image`
 * - background / mediaAlignment — CMS only (`mediaAlignment` containing "left" → mediaReversed)
 */
function renderPlaceholderModelStory({ item, sysId, extraData }: CmsRenderContext): ReactNode {
  const placeholderItem = item as CMSPlaceholderModelStory;
  const productDetails = extraData?.productDetails as ProductStoryDetailsExtra | undefined;

  const resolved = resolveModelStoryViewModel({
    cms: {
      storyTitle: placeholderItem.storyTitle,
      storyContent:
        typeof placeholderItem.storyContent === 'string' ? placeholderItem.storyContent : null,
      media: placeholderItem.media,
      mediaAlignment: placeholderItem.mediaAlignment,
      backgroundColor: placeholderItem.backgroundColor,
    },
    saleor: productDetails?.modelStory,
    productName: productDetails?.product?.name,
    productSku: productDetails?.product?.sku,
    productType: productDetails?.productType,
  });

  // Always render the CMS shell (layout/bg/alignment) even when Saleor fill is empty.
  return (
    <ProductStory
      key={sysId}
      title={resolved.title}
      content={resolved.content}
      image={resolved.image}
      imageAlign={resolved.imageAlign}
      mediaReversed={resolved.mediaReversed}
      backgroundColor={resolved.backgroundColor}
    />
  );
}

function renderTutorialBanner({ item, sysId, cmsIndex }: CmsRenderContext): ReactNode {
  return <TutorialBanner key={sysId} data={item as never} cmsIndex={cmsIndex} />;
}

// ============================================================================
// Placeholder sub-type renderers (dispatched by `placeholderType`)
// ============================================================================

function renderStrapProductGridPlaceholder({
  sysId,
  extraData,
  cmsIndex,
}: CmsRenderContext): ReactNode {
  // ProductGrid uses extraData for strap product listing
  // Note: ProductGrid is just a grid - heading/breadcrumbs handled at page level
  if (!extraData?.productGrid) {
    devWarn('Missing productGrid data for placeholder');
    return null;
  }
  const strapGridData = extraData.productGrid as {
    products: unknown[];
    productLabel?: string;
    productType?: string;
    pagination?: { currentPage: number; totalPages: number };
    filters?: {
      facets: Record<string, Record<string, number>>;
      activeFilters: Record<string, unknown>;
      totalResults: number;
      position?: FilterBarPosition;
      allFacets?: Record<string, Record<string, number>>;
      currentSort?: string;
      productLabel?: string;
      hideControls?: boolean;
    };
  };

  const strapInlineFilterBar =
    strapGridData.filters?.position === 'inline' ? (
      <ListingFilters
        facets={strapGridData.filters.facets as never}
        activeFilters={strapGridData.filters.activeFilters as never}
        totalResults={strapGridData.filters.totalResults}
        position="inline"
        allFacets={strapGridData.filters.allFacets as never}
        currentSort={(strapGridData.filters.currentSort || 'relevance') as never}
        productLabel={strapGridData.filters.productLabel as string | undefined}
        hideControls={strapGridData.filters.hideControls}
      />
    ) : undefined;

  const strapAllFacets = strapGridData.filters?.allFacets || strapGridData.filters?.facets;
  const materialFacetKey = strapAllFacets
    ? Object.keys(strapAllFacets).find((k) => k.startsWith('material'))
    : undefined;
  const materialFacets = materialFacetKey ? strapAllFacets![materialFacetKey] : undefined;

  const strapBeforeGrid = (
    <>
      {materialFacets && <MaterialTabs materialFacets={materialFacets} />}
      {strapInlineFilterBar}
    </>
  );

  // Handler to open strap drawer for PLP straps (instead of adding directly to cart)
  const handleStrapGridAddToBag = (product: {
    variantId?: string;
    sku: string;
    name: string;
    image: string;
    price: string | number;
    priceAmount?: number;
    currency?: string;
    href: string;
    integratedBuckle?: boolean;
    addCtaType?: string;
    ctaType?: string;
  }) => {
    const slug = product.href?.split('/').pop() || '';
    const priceNum =
      typeof product.price === 'number'
        ? product.price
        : parseFloat(String(product.price).replace(/[^0-9.]/g, '')) || 0;
    const locale = extraData?.locale as string | undefined;
    const channelSlug = locale ? localeConfig.getChannelSlug(locale) : 'us-usd';

    modalActions.open('cart-strap-drawer', {
      orderCtaType: (product.addCtaType ?? product.ctaType ?? 'ORDER') as 'ORDER' | 'PRE_ORDER',
      channel: channelSlug,
      selectedStrapVariantId: product.variantId,
      selectedStrapSku: product.sku,
      selectedStrapName: product.name,
      selectedStrapImage: product.image,
      selectedStrapPrice: priceNum,
      selectedStrapCurrency: product.currency,
      selectedStrapSlug: slug,
      hasIntegratedBuckle: product.integratedBuckle ?? false,
    });
  };

  return (
    <ProductGrid
      key={sysId}
      products={strapGridData.products as never}
      beforeGrid={strapBeforeGrid}
      cmsIndex={cmsIndex}
      pagination={strapGridData.pagination}
      productLabel={strapGridData.productLabel}
      productType={strapGridData.productType as 'Watch' | 'Strap' | 'Eyewear' | undefined}
      onAddToBag={handleStrapGridAddToBag}
    />
  );
}

function renderTrendingProductsPlaceholder({
  item,
  sysId,
  extraData,
  cmsIndex,
}: CmsRenderContext): ReactNode {
  // TrendingProducts uses extraData for product data (like PDP's "You May Also Like")
  if (!extraData?.trendingProducts) {
    devWarn('Missing trendingProducts data for placeholder');
    return null;
  }
  // Handle both formats: TrendingProductsFragment (productsCollection.items) and CMSTrendingProductsType (products)
  const trendingProductsData = extraData.trendingProducts as {
    heading?: CMSTrendingProductsType['heading'];
    products?: CMSProductCard[];
    productsCollection?: {
      items: Array<{
        sys?: { id: string };
        name?: string;
        subtitle?: string;
        sku?: string;
        price?: number;
        currency?: string;
        image?: { url?: string; alt?: string };
        href?: string;
      }>;
    };
  };
  // Normalize products from either format
  const trendingProducts: CMSProductCard[] =
    trendingProductsData.products ||
    (trendingProductsData.productsCollection?.items || []).map((p) => ({
      sys: { id: p.sys?.id || '', publishedAt: null },
      name: p.name || '',
      subtitle: p.subtitle || null,
      sku: p.sku || null,
      price: p.price || null,
      currency: p.currency || null,
      image: p.image
        ? {
            url: p.image.url || '',
            alt: p.image.alt || null,
            title: null,
            description: null,
            width: null,
            height: null,
            contentType: null,
            focalPoint: null,
          }
        : null,
      href: p.href || null,
    }));

  if (trendingProducts.length === 0) {
    devWarn('No products found in trendingProducts data');
    return null;
  }

  const placeholderHeading = (item as { placeholderHeading?: string }).placeholderHeading;
  const trendingHeadingData = placeholderHeading
    ? { ...trendingProductsData.heading, text: placeholderHeading }
    : trendingProductsData.heading;
  return (
    <TrendingProducts
      key={sysId}
      headingData={trendingHeadingData || undefined}
      products={trendingProducts}
      automaticType="you may also like"
      cmsIndex={cmsIndex}
    />
  );
}

function renderExtraStrapsPlaceholder({
  item,
  sysId,
  extraData,
  cmsIndex,
}: CmsRenderContext): ReactNode {
  // TrendingProducts with compatible straps type for "Add an Extra Strap"
  if (!extraData?.extraStraps) {
    // No straps data available - silent return (straps may not be configured)
    return null;
  }
  const extraStrapsData = extraData.extraStraps as CMSTrendingProductsType;
  const extraStrapsPlaceholder = (item as { placeholderHeading?: string }).placeholderHeading;
  const extraStrapsHeadingData = extraStrapsPlaceholder
    ? { ...extraStrapsData.heading, text: extraStrapsPlaceholder }
    : extraStrapsData.heading || { text: 'Add an Extra Strap' };
  return (
    <TrendingProducts
      key={sysId}
      headingData={extraStrapsHeadingData}
      products={extraStrapsData.products}
      automaticType="compatible straps"
      cmsIndex={cmsIndex}
    />
  );
}

function renderEBoutiqueServicesPlaceholder({
  item,
  sysId,
  extraData,
  cmsIndex,
}: CmsRenderContext): ReactNode {
  // EBoutiqueServices uses the placeholder heading for the title
  const eBoutiqueData = extraData?.eBoutiqueServices as { services?: unknown[] } | undefined;
  return (
    <EBoutiqueServices
      key={sysId}
      data={
        {
          __typename: 'EBoutiqueServices',
          sys: item.sys,
          heading: (item as { placeholderHeading?: string }).placeholderHeading,
          services: eBoutiqueData?.services,
        } as never
      }
      cmsIndex={cmsIndex}
    />
  );
}

function renderProductListingPlaceholder({
  item,
  sysId,
  extraData,
  cmsIndex,
}: CmsRenderContext): ReactNode {
  // ProductGrid uses extraData for watch product listing
  // Note: ProductGrid is just a grid - heading/breadcrumbs handled at page level
  // Optionally includes inline filter bar via beforeGrid slot
  if (!extraData?.productGrid) {
    devWarn('Missing productGrid data for placeholder');
    return null;
  }
  // Editorial card overrides come from the Placeholder entry itself
  // (CMSPlaceholder.tileOverrides). Forward them so ProductGrid can
  // place them at the requested (row, columnIndex, span).
  const tileOverrides = (item as { tileOverrides?: unknown[] | null }).tileOverrides;
  const watchGridData = extraData.productGrid as {
    products: unknown[];
    onProductClick?: (id: string, position: number) => void;
    // Product type for terminology
    productLabel?: string;
    productType?: string;
    // Pagination data
    pagination?: {
      currentPage: number;
      totalPages: number;
    };
    // Optional inline filters
    filters?: {
      facets: Record<string, Record<string, number>>;
      activeFilters: Record<string, unknown>;
      totalResults: number;
      position?: FilterBarPosition;
      allFacets?: Record<string, Record<string, number>>;
      currentSort?: string;
      productLabel?: string;
      hideControls?: boolean;
    };
  };
  // CMS Placeholder.initiallyCollapsed — ProductGrid also expands when the
  // URL already has listing chrome (page / filters / sort).
  const initiallyCollapsed = Boolean(
    (item as { initiallyCollapsed?: boolean | null }).initiallyCollapsed
  );
  // Build inline filter bar if filters provided and position is 'inline'
  const inlineFilterBar =
    watchGridData.filters?.position === 'inline' ? (
      <ListingFilters
        facets={watchGridData.filters.facets as never}
        activeFilters={watchGridData.filters.activeFilters as never}
        totalResults={watchGridData.filters.totalResults}
        position="inline"
        allFacets={watchGridData.filters.allFacets as never}
        currentSort={(watchGridData.filters.currentSort || 'relevance') as never}
        productLabel={watchGridData.filters.productLabel as string | undefined}
        hideControls={watchGridData.filters.hideControls}
      />
    ) : undefined;

  return (
    <ProductGrid
      key={sysId}
      products={watchGridData.products as never}
      onProductClick={watchGridData.onProductClick}
      beforeGrid={inlineFilterBar}
      cmsIndex={cmsIndex}
      pagination={watchGridData.pagination}
      productLabel={watchGridData.productLabel as string | undefined}
      productType={watchGridData.productType as 'Watch' | 'Strap' | 'Eyewear' | undefined}
      tileOverrides={tileOverrides as never}
      initiallyCollapsed={initiallyCollapsed}
    />
  );
}

function renderKeyFeaturesPlaceholder({ sysId, extraData }: CmsRenderContext): ReactNode {
  const productDetails = extraData?.productDetails as
    | {
        keyFeatures?: Array<{
          id: string;
          label: string;
          value: string;
          icon?: string;
          swatchColor?: string;
        }>;
        manualUrl?: string;
      }
    | undefined;

  if (!productDetails?.keyFeatures) {
    devWarn('Missing productDetails.keyFeatures for placeholder');
    return null;
  }
  return (
    <KeyFeatures
      key={sysId}
      features={productDetails.keyFeatures}
      manualUrl={productDetails.manualUrl}
    />
  );
}

function renderTechnicalDataPlaceholder({ sysId, extraData }: CmsRenderContext): ReactNode {
  const productDetails = extraData?.productDetails as
    | {
        technicalData?: Array<{
          title: string;
          items: Array<{ label: string; value: string }>;
        }>;
      }
    | undefined;

  if (!productDetails?.technicalData) {
    devWarn('Missing productDetails.technicalData for placeholder');
    return null;
  }
  return <ProductTechnicalData key={sysId} sections={productDetails.technicalData} />;
}

function renderRegisterWatchPlaceholder({ sysId }: CmsRenderContext): ReactNode {
  return <RegisterWatch key={sysId} />;
}

function renderWarrantyPlaceholder({ item, sysId, extraData }: CmsRenderContext): ReactNode {
  // Warranty section - two-column layout with warranty details and care info
  // Data comes from both:
  // 1. Product details (warrantyDuration, movementManufacture)
  // 2. Adapted CMSPlaceholder (warrantyText, warrantyTakingCareOfYourWatch)
  const productDetails = extraData?.productDetails as
    | {
        product?: {
          warrantyDuration?: number;
          warrantyExtDuration?: number;
          movementManufacture?: string;
        };
      }
    | undefined;

  // The adapter now properly populates these fields on CMSPlaceholder
  const placeholderItem = item as CMSPlaceholder;

  // =======================================================================
  // TODO: GALLET WARRANTY SECTION HIDDEN
  // The warranty section is currently hidden for Gallet because we don't
  // have designs for it yet. Once Gallet warranty designs are available,
  // remove this brand check to enable the warranty section.
  // =======================================================================
  // UG is Gallet-shaped, so it hides the warranty section like Gallet.
  if (IS_GALLET_SHAPED) {
    return null;
  }

  return (
    <Warranty
      key={sysId}
      heading={placeholderItem.heading || undefined}
      subtext={placeholderItem.subtext}
      warrantyDuration={productDetails?.product?.warrantyDuration}
      warrantyExtDuration={productDetails?.product?.warrantyExtDuration}
      movementManufacture={productDetails?.product?.movementManufacture}
      warrantyText={placeholderItem.warrantyText}
      warrantyTakingCareOfYourWatch={placeholderItem.warrantyTakingCareOfYourWatch}
    />
  );
}

/**
 * UG's product rail, shared by two surfaces: the PDP's related-watches row and
 * the couture collection page. Live uses one component for both — same card,
 * same chevrons, same geometry at every width — so this renders one here too.
 *
 * Catalogue-only: no price, no add-to-cart, which is why it is not the shared
 * `trending products` placeholder. Both callers hand over cards already shaped
 * by `buildUgCarouselItems`, so this is a straight pass-through. Resolves to
 * `RenderNull` on Gallet and Breitling via the brand router.
 */
function renderUgProductsCarouselPlaceholder({
  item,
  sysId,
  extraData,
}: CmsRenderContext): ReactNode {
  const carousel = extraData?.ugProductsCarousel as
    | { items?: UgCollectionProductsProps['items'] }
    | undefined;

  if (!carousel?.items?.length) {
    return null;
  }

  return (
    <CollectionProducts
      key={sysId}
      items={carousel.items}
      heading={(item as CMSPlaceholder).heading ?? undefined}
    />
  );
}

/**
 * CMS-authored `Breadcrumb` block (BEC-24794), rendered through the content
 * pipeline. The entry controls placement and style only: `ProductListingPage`
 * supplies the crumbs via `extraData.breadcrumbs` — either because a content
 * manager authored a Breadcrumb entry (rendered at its authored position), or
 * because the page injected a synthetic one right after the first HeroBanner
 * card. `style: 'Light'` inverts the bar for dark contexts (legacy semantic).
 */
function renderBreadcrumb({ item, sysId, extraData }: CmsRenderContext): ReactNode {
  const breadcrumbs = extraData?.breadcrumbs as Array<{ label: string; href?: string }> | undefined;
  if (!breadcrumbs?.length) {
    devWarn('Missing breadcrumbs for CMS Breadcrumb block');
    return null;
  }
  const style = (item as { style?: string | null }).style;
  return <PdpBreadcrumbsBar key={sysId} items={breadcrumbs} inverted={style === 'Light'} />;
}

// UG "Stay Informed" newsletter form (BEC-24123).
//
// The authored copy is fetched by the route, not here: this renderer runs inside
// a client component, so it reads what the server put in `extraData`. An absent
// value is not an error — every string falls back to the message catalogue, so
// the form still renders complete.
function renderNewsletterFormPlaceholder({ item, sysId, extraData }: CmsRenderContext): ReactNode {
  const copy = extraData?.[UG_NEWSLETTER_EXTRA_DATA_KEY] as CMSFormCopy | null | undefined;

  return (
    <NewsletterForm key={sysId} copy={copy} heading={(item as { heading?: string }).heading} />
  );
}

/** Dispatch table for `Placeholder` items, keyed by lowercased `placeholderType`. */
const PLACEHOLDER_RENDERERS: Record<string, (ctx: CmsRenderContext) => ReactNode> = {
  'strap product grid': renderStrapProductGridPlaceholder,
  'trending products': renderTrendingProductsPlaceholder,
  'extra straps': renderExtraStrapsPlaceholder,
  'e-boutique services': renderEBoutiqueServicesPlaceholder,
  'product listing': renderProductListingPlaceholder,
  'key features': renderKeyFeaturesPlaceholder,
  'technical data': renderTechnicalDataPlaceholder,
  'register watch': renderRegisterWatchPlaceholder,
  'ug related products': renderUgProductsCarouselPlaceholder,
  warranty: renderWarrantyPlaceholder,
  // Same renderer as 'ug related products' above: live uses one rail on the PDP
  // and the collection page, and so do we.
  [COLLECTION_PRODUCTS_PLACEHOLDER]: renderUgProductsCarouselPlaceholder,
  [NEWSLETTER_FORM_PLACEHOLDER]: renderNewsletterFormPlaceholder,
};

// ============================================================================
// Placeholder - Render static components based on type
// ============================================================================
// Use string comparison to handle types from REST API that may not be in the fragment union
function renderPlaceholder(ctx: CmsRenderContext): ReactNode {
  const { item, sysId, cmsIndex, extraData } = ctx;
  const placeholderType = (item as { type?: string }).type?.trim().toLowerCase();
  const boutiquePlaceholder = renderBoutiquePlaceholder(
    placeholderType,
    item,
    sysId,
    cmsIndex,
    extraData
  );

  if (boutiquePlaceholder !== undefined) {
    return boutiquePlaceholder;
  }

  const placeholderRenderer = placeholderType ? PLACEHOLDER_RENDERERS[placeholderType] : undefined;
  if (placeholderRenderer) {
    return placeholderRenderer(ctx);
  }

  devWarn(`Unknown placeholder type: ${placeholderType}`);
  return null;
}

/**
 * Dispatch table mapping a CMS item's `__typename` to its renderer. Adding a new
 * component type is a single entry here plus its `render*` function above; the
 * `CMSComponent` dispatcher itself never grows.
 */
const COMPONENT_RENDERERS: Record<string, (ctx: CmsRenderContext) => ReactNode> = {
  HeroSlider: renderHeroSlider,
  Heading: renderHeading,
  ServicesCollection: renderServices,
  Services: renderServices,
  FaqWrap: renderFaqWrap,
  Banner: renderBanner,
  ContactUs: renderContactUs,
  CollectionGroup: renderCollectionGroup,
  FindAStore: renderFindAStore,
  PlaceholderStores: renderPlaceholderStores,
  FreeTextContent: renderFreeTextContent,
  Grid: renderGrid,
  UniversalGrid: renderUniversalGrid,
  Iframe: renderIframe,
  NavQuickLinks: renderNavQuickLinks,
  NavQuickLinksWithBanner: renderNavQuickLinks,
  PlaceholderService: renderPlaceholderServiceComponent,
  FaqCategories: renderFaqCategories,
  HistoryNavigations: renderHistoryNavigations,
  ConditionalComponent: renderConditionalComponent,
  SectionHeader: renderSectionHeader,
  NewsHeader: renderNewsHeader,
  PlaceholderPrint: renderPlaceholderPrint,
  CollectionInfoParameter: renderCollectionInfoParameter,
  MyAccountCardGroup: renderMyAccountCardGroup,
  MediaWrapper: renderMediaWrapper,
  ImageWithFocalPoint: renderMediaWrapper,
  DoubleCard: renderDoubleCard,
  DoubleCardWide: renderDoubleCardWide,
  ThreeColumnStack: renderThreeColumnStack,
  ThreeColumnScroll: renderThreeColumnScroll,
  FourColumnScroll: renderFourColumnScroll,
  SliderMaxFourCards: renderSliderMaxFourCards,
  SliderFullwidthGallery: renderSliderFullwidthGallery,
  SliderProductsManual: renderSliderProducts,
  SliderProductsAutomatic: renderSliderProducts,
  AutomaticCarousel: renderSliderProducts,
  Card: renderCard,
  ContentGroup: renderContentGroup,
  ImageTriptych: renderImageTriptych,
  [CMSComponentType.DoubleBanner]: renderDoubleBanner,
  [CMSComponentType.CategoryBannerGrid]: renderCategoryBannerGrid,
  [CMSComponentType.TrendingProducts]: renderTrendingProducts,
  [CMSComponentType.StoriesBanner]: renderStoriesBanner,
  [CMSComponentType.StoriesList]: renderStoriesList,
  [CMSComponentType.Timeline]: renderTimeline,
  [CMSComponentType.EBoutiqueServices]: renderEBoutiqueServices,
  [CMSComponentType.Popup]: renderPopup,
  PlaceholderProductDetails: renderPlaceholderProductDetails,
  PlaceholderEyewearDetails: renderPlaceholderEyewearDetails,
  PlaceholderModelStory: renderPlaceholderModelStory,
  TutorialBanner: renderTutorialBanner,
  PlaceholderBoutiquesInfo: ({ sysId, cmsIndex, extraData }) =>
    renderPlaceholderBoutiquesInfo(sysId, cmsIndex, extraData),
  PlaceholderBoutiquesAbout: ({ sysId, extraData }) =>
    renderPlaceholderBoutiqueDetailAbout(sysId, extraData),
  PlaceholderBoutiquesList: ({ sysId, extraData }) =>
    renderPlaceholderBoutiquesList(sysId, extraData),
  Placeholder: renderPlaceholder,
  placeholder: renderPlaceholder,
  Breadcrumb: renderBreadcrumb,
};

/**
 * Renders a single CMS content item based on its __typename
 *
 * Component Architecture (following Breitling pattern):
 *
 * 1. **Card** - Polymorphic content card dispatched by `type` field
 *    (HeroBanner, IntroText, MediaAndSideText, MediaBanner, Text, Story, SectionBanner, etc.)
 *
 * 2. **ContentGroup** - Multi-item layout containers
 *    (ImageTriptych → ThreeColumnStack with MediaWrapper items)
 *
 * 3. **Gallet-specific** - Complex components that stay separate:
 *    DoubleBanner, CategoryBannerGrid, TrendingProducts, StoriesBanner,
 *    EBoutiqueServices, PromoBanner
 *
 * Dispatch is table-driven via `COMPONENT_RENDERERS`; each `__typename` maps to a
 * small, focused `render*` function above.
 */
function CMSComponent({
  item,
  extraData,
  index,
}: Readonly<{
  item: CMSContentItem;
  extraData?: CMSRendererExtraData;
  index: number;
}>) {
  // Cast to string to allow comparison with all possible CMS types
  // The ContentfulFragment type is a subset, but CMS can return many more types
  const typename = item.__typename as string;

  const renderer = COMPONENT_RENDERERS[typename];
  if (renderer) {
    return renderer({
      item,
      extraData,
      typename,
      sysId: item.sys?.id ?? '',
      isFirstItem: index === 0,
      // Pass index to components for unique data-testid generation
      cmsIndex: index,
    });
  }

  // ==========================================================================
  // Unknown type
  // ==========================================================================
  devWarn(`Unknown CMS component type: ${typename}`);
  return null;
}

function getCmsItemKey(item: CMSContentItem, index: number): string {
  const id = item.sys?.id?.trim();
  return id || `cms-entry-${index}`;
}

function isRenderableCmsItem(item: CMSContentItem | null | undefined): item is CMSContentItem {
  return item?.sys?.id != null;
}

/** Inner renderer that reads the ?debug=cms search param. */
function CMSRendererInner({ content, extraData }: CMSRendererProps) {
  const debug = useDebugCms();

  if (!content || content.length === 0) {
    return null;
  }

  const validContent = content.filter(isRenderableCmsItem);

  if (!debug) {
    return (
      <>
        {validContent.map((item, index) => {
          return (
            <EntryEffectWrapper
              key={getCmsItemKey(item, index)}
              enabled={isEntryEffectEnabled(item)}
              variant={getEntryEffectVariant(item)}
            >
              <CMSComponent item={item} extraData={extraData} index={index} />
            </EntryEffectWrapper>
          );
        })}
      </>
    );
  }

  return (
    <>
      {validContent.map((item, index) => {
        // Call as plain function (not JSX) so we can detect null returns
        const rendered = CMSComponent({ item, extraData, index });

        if (rendered === null) {
          return <DebugNullLabel key={getCmsItemKey(item, index)} item={item} index={index} />;
        }

        return (
          <DebugLabel key={getCmsItemKey(item, index)} item={item} index={index}>
            <EntryEffectWrapper
              enabled={isEntryEffectEnabled(item)}
              variant={getEntryEffectVariant(item)}
            >
              {rendered}
            </EntryEffectWrapper>
          </DebugLabel>
        );
      })}
    </>
  );
}

/**
 * Renders an array of CMS content items.
 *
 * Append ?debug=cms to any page URL to display labelled overlays
 * showing each component's __typename, variant, and Contentful
 * internalName. Components that render null are shown as red
 * striped placeholders.
 */
export function CMSRenderer(props: CMSRendererProps) {
  const servicePrices = props.extraData?.servicePrices;

  return (
    <ServicePriceLookupInitialProvider value={servicePrices ?? null}>
      <CMSRendererInner {...props} />
    </ServicePriceLookupInitialProvider>
  );
}

CMSRenderer.displayName = 'CMSRenderer';
